/**
 * OCP-930 - operator approvals: the agent path for person-level actions
 * (operator decision 2026-10-21, "agent with approval").
 *
 *   /approvals       the requests: pending, approved, denied, expired, consumed
 *   /approvals/:id   one request: the exact action in plain language, the
 *                    requesting bot and its reason; Approve with a FRESH
 *                    authenticator code, or Deny with a reason.
 *
 * API: GET /v1/admin/approvals, GET /v1/admin/approvals/:id,
 * POST /v1/admin/approvals/:id/approve { totp_code }, POST .../deny { reason }.
 * Deciding is a super_admin person in this admin session; there is no MCP
 * tool and no token path for it, by design.
 */
import { useCallback, useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';

import { api, ApiError } from '../lib/api';
import { fmtDateTime } from '../lib/format-time';

type ApprovalStatus = 'pending' | 'approved' | 'denied' | 'expired' | 'consumed';

interface Approval {
  id: string;
  action: string;
  route: string;
  summary: string;
  details: Array<{ label: string; value: string }>;
  /** super_admin readers only (OCP-930 review). */
  binding_digest?: string;
  confirmation_jti?: string | null;
  requested_by: string;
  request_reason: string;
  request_source: string;
  status: ApprovalStatus;
  created_at: string;
  expires_at: string;
  decided_at: string | null;
  decided_by: string | null;
  decision_reason: string | null;
  consumed_at: string | null;
}

const STATUSES: ApprovalStatus[] = ['pending', 'approved', 'denied', 'expired', 'consumed'];

const STATUS_STYLE: Record<ApprovalStatus, string> = {
  pending: 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-200',
  approved: 'bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-200',
  denied: 'bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-200',
  expired: 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300',
  consumed: 'bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-200',
};

function StatusBadge({ status }: { status: ApprovalStatus }) {
  return <span data-testid="approval-status" className={`rounded px-1.5 py-0.5 text-[11px] font-medium uppercase tracking-wide ${STATUS_STYLE[status]}`}>{status}</span>;
}

function errorText(err: unknown, fallback: string): string {
  if (!(err instanceof ApiError)) return fallback;
  const b = err.body as { error?: string; message?: string } | null;
  if (b?.error === 'invalid_code') return 'The code does not match - enter the current code of your authenticator app.';
  if (b?.error === 'approval_expired') return 'The request expired - the agent has to ask again.';
  if (b?.error === 'approval_needs_a_person') return 'Approvals are decided by a person signed in here.';
  if (b?.error === 'forbidden_for_role') return 'Only a super_admin decides approval requests.';
  if (b?.error === 'no_second_factor') return 'Set up your second factor (Security) before you approve.';
  if (b?.error === 'throttled') return 'Too many wrong codes - wait a moment and retry.';
  return b?.message ?? `${fallback} (HTTP ${err.status})`;
}

export function AdminApprovalsPage() {
  const [status, setStatus] = useState<ApprovalStatus | ''>('pending');
  const [items, setItems] = useState<Approval[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setError(null);
    try {
      const res = await api<{ items: Approval[] }>(`/v1/admin/approvals${status ? `?status=${status}` : ''}`);
      setItems(res.items);
    } catch (err) {
      setError(errorText(err, 'Load failed'));
    }
  }, [status]);

  useEffect(() => {
    void load();
  }, [load]);

  return (
    <div className="space-y-4">
      <div>
        <h1 className="text-lg font-semibold">Approvals</h1>
        <p className="mt-1 text-xs text-gray-500">
          Agents run person-level actions (SSH changes, host-key pins, file-backup setup and restore, decrypted payout details,
          bulk exports) only after a super_admin approves the exact action here with a fresh second-factor code.
        </p>
      </div>
      <div className="flex flex-wrap gap-2" role="tablist">
        {(['', ...STATUSES] as const).map((s) => (
          <button
            key={s || 'all'}
            type="button"
            onClick={() => setStatus(s)}
            className={`rounded border px-2.5 py-1 text-xs ${status === s ? 'border-brand-500 text-brand-700' : 'border-gray-300 dark:border-gray-700 text-gray-600 dark:text-gray-400'}`}
          >
            {s || 'all'}
          </button>
        ))}
      </div>
      {error && <div role="alert" className="text-sm text-red-700 dark:text-red-300">{error}</div>}
      {items && items.length === 0 && <div className="text-sm text-gray-500">No {status || ''} requests.</div>}
      {items && items.length > 0 && (
        <table className="w-full text-sm" data-testid="approvals-table">
          <thead>
            <tr className="text-left text-xs uppercase tracking-wide text-gray-500">
              <th className="py-1 pr-3">Status</th>
              <th className="py-1 pr-3">Action</th>
              <th className="py-1 pr-3">Requested by</th>
              <th className="py-1 pr-3">Requested</th>
              <th className="py-1 pr-3">Expires</th>
            </tr>
          </thead>
          <tbody>
            {items.map((a) => (
              <tr key={a.id} className="border-t border-gray-100 dark:border-gray-800">
                <td className="py-1.5 pr-3"><StatusBadge status={a.status} /></td>
                <td className="py-1.5 pr-3"><Link className="text-brand-600 hover:underline" to={`/approvals/${a.id}`}>{a.summary}</Link></td>
                <td className="py-1.5 pr-3 text-xs">{a.requested_by}</td>
                <td className="py-1.5 pr-3 text-xs">{fmtDateTime(a.created_at)}</td>
                <td className="py-1.5 pr-3 text-xs">{fmtDateTime(a.expires_at)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

export function AdminApprovalDetailPage() {
  const { id } = useParams<{ id: string }>();
  const [approval, setApproval] = useState<Approval | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [code, setCode] = useState('');
  const [denyReason, setDenyReason] = useState('');
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      setApproval((await api<{ approval: Approval }>(`/v1/admin/approvals/${id}`)).approval);
    } catch (err) {
      setError(errorText(err, 'Load failed'));
    }
  }, [id]);

  useEffect(() => {
    void load();
  }, [load]);

  async function decide(kind: 'approve' | 'deny') {
    if (busy) return;
    if (kind === 'approve' && !/^\d{6}$/.test(code.trim())) return setError('Enter the 6-digit code of your authenticator app.');
    if (kind === 'deny' && denyReason.trim().length < 10) return setError('Name why you deny it (at least 10 characters) - the agent and the audit log see it.');
    setBusy(true);
    setError(null);
    try {
      const res = await api<{ approval: Approval }>(`/v1/admin/approvals/${id}/${kind}`, {
        method: 'POST',
        body: kind === 'approve' ? { totp_code: code.trim() } : { reason: denyReason.trim() },
      });
      setApproval(res.approval);
      setCode('');
    } catch (err) {
      setError(errorText(err, kind === 'approve' ? 'Approve failed' : 'Deny failed'));
    } finally {
      setBusy(false);
    }
  }

  const input = 'rounded border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900 px-2 py-1 text-sm';
  return (
    <div className="space-y-4">
      <Link to="/approvals" className="text-xs text-brand-600 hover:underline">All approvals</Link>
      {error && <div role="alert" className="text-sm text-red-700 dark:text-red-300">{error}</div>}
      {approval && (
        <>
          <div className="flex flex-wrap items-center gap-2">
            <h1 className="text-lg font-semibold">{approval.summary}</h1>
            <StatusBadge status={approval.status} />
          </div>
          <section className="rounded-lg border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 p-4 text-sm" data-testid="approval-details">
            <dl className="grid grid-cols-[auto,1fr] gap-x-4 gap-y-1">
              <dt className="text-gray-500">Requested by</dt>
              <dd>{approval.requested_by} ({approval.request_source})</dd>
              <dt className="text-gray-500">Reason given</dt>
              <dd data-testid="approval-reason">{approval.request_reason}</dd>
              {approval.details.map((d) => (
                <div key={d.label} className="contents">
                  <dt className="text-gray-500">{d.label}</dt>
                  <dd className="break-all">{d.value}</dd>
                </div>
              ))}
              <dt className="text-gray-500">Requested</dt>
              <dd>{fmtDateTime(approval.created_at)}</dd>
              <dt className="text-gray-500">Expires</dt>
              <dd>{fmtDateTime(approval.expires_at)}</dd>
              {approval.decided_by && (
                <>
                  <dt className="text-gray-500">Decided</dt>
                  <dd>{approval.decided_by}, {approval.decided_at ? fmtDateTime(approval.decided_at) : ''}{approval.decision_reason ? ` - ${approval.decision_reason}` : ''}</dd>
                </>
              )}
              <dt className="text-gray-500">Binding</dt>
              <dd className="font-mono text-xs">
                {approval.route}
                {approval.binding_digest ? ` - sha256 ${approval.binding_digest.slice(0, 16)}` : ''}
                {approval.confirmation_jti ? ` - token ${approval.confirmation_jti}` : ''}
              </dd>
            </dl>
          </section>
          {approval.status === 'pending' && (
            <section className="space-y-3 rounded-lg border border-amber-300 dark:border-amber-800 bg-amber-50 dark:bg-amber-900/20 p-4 text-sm">
              <p className="text-xs text-amber-900 dark:text-amber-200">
                Approving lets {approval.requested_by} run exactly this action once, until {fmtDateTime(approval.expires_at)}. Check every line above;
                deny anything you cannot verify.
              </p>
              <div className="flex flex-wrap items-end gap-2">
                <label className="text-xs text-gray-600 dark:text-gray-400">
                  Authenticator code (fresh)
                  <input
                    aria-label="Authenticator code"
                    inputMode="numeric"
                    autoComplete="one-time-code"
                    value={code}
                    onChange={(e) => setCode(e.target.value)}
                    className={`${input} mt-0.5 block w-32 font-mono`}
                  />
                </label>
                <button type="button" disabled={busy} onClick={() => void decide('approve')} className="rounded bg-green-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-green-700 disabled:opacity-50">
                  Approve
                </button>
              </div>
              <div className="flex flex-wrap items-end gap-2">
                <label className="flex-1 text-xs text-gray-600 dark:text-gray-400">
                  Deny reason
                  <input aria-label="Deny reason" value={denyReason} onChange={(e) => setDenyReason(e.target.value)} className={`${input} mt-0.5 block w-full`} />
                </label>
                <button type="button" disabled={busy} onClick={() => void decide('deny')} className="rounded border border-red-400 px-3 py-1.5 text-sm text-red-700 hover:bg-red-50 dark:text-red-300 disabled:opacity-50">
                  Deny
                </button>
              </div>
            </section>
          )}
        </>
      )}
    </div>
  );
}
